// =============================================================================
// ONGLET DOCUMENTS AIDES - Vue Devis
// =============================================================================
// Console réglementaire pour la génération et le suivi des documents CEE/MPR
// 
// 3 BLOCS :
// 1. Documents autorisés (selon statut du devis)
// 2. Historique des versions
// 3. Statut documentaire (récapitulatif)
//
// RÈGLES D'IMMUABILITÉ (CORRIGÉES) :
// - Génération possible dès statut VALIDATION (pas besoin LOCKED)
// - Signature = traçabilité, PAS verrouillage
// - Documents modifiables (avec versioning) jusqu'à PAYE
// - Verrouillage définitif UNIQUEMENT au statut PAYE
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  FileText,
  Download,
  Eye,
  Lock,
  Unlock,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Clock,
  Hash,
  Calendar,
  User,
  FileSignature,
  ShieldCheck,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Loader2,
  FileCheck,
  FileOutput,
  Printer
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { formatDateTimeFR } from "../../utils/dateFormat";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// CONFIGURATION DES STATUTS
// =============================================================================

// ORDRE: DRAFT < VALIDATION < LOCKED < DEVIS_SIGNE < CHANTIER_TERMINE < DOSSIER_DEPOSE < PAYE
// Note: Génération de documents possible dès VALIDATION (ou GENERE pour TEF)
const STATUTS_ORDRE = {
  "draft": 0,
  "DRAFT": 0,
  "genere": 1,
  "GENERE": 1,
  "validation": 1,
  "VALIDATION": 1,
  "locked": 2,
  "LOCKED": 2,
  "LOCKED_DEVIS": 2,
  "devis_signe": 3,
  "DEVIS_SIGNE": 3,
  "chantier_termine": 4,
  "CHANTIER_TERMINE": 4,
  "dossier_depose": 5,
  "DOSSIER_DEPOSE": 5,
  "paye": 6,
  "PAYE": 6
};

const STATUT_LABELS = {
  "draft": "Brouillon",
  "DRAFT": "Brouillon",
  "genere": "Généré",
  "GENERE": "Généré",
  "validation": "En validation",
  "VALIDATION": "En validation",
  "locked": "Verrouillé",
  "LOCKED": "Verrouillé",
  "devis_signe": "Devis signé",
  "DEVIS_SIGNE": "Devis signé",
  "LOCKED_DEVIS": "Devis verrouillé",
  "chantier_termine": "Chantier terminé",
  "CHANTIER_TERMINE": "Chantier terminé",
  "dossier_depose": "Dossier déposé",
  "DOSSIER_DEPOSE": "Dossier déposé",
  "paye": "Payé",
  "PAYE": "Payé"
};

// Phases des documents
const PHASES = {
  "avant_travaux": { label: "Avant travaux", color: "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]", icon: Clock },
  "apres_travaux": { label: "Après travaux", color: "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]", icon: CheckCircle2 }
};

// Types de documents
const TYPES_DOCUMENT = {
  "cadre_contribution": { label: "Cadre de Contribution CEE", icon: FileText, aide: "CEE" },
  "attestation_honneur_cee": { label: "Attestation sur l'Honneur CEE", icon: FileSignature, aide: "CEE" },
  "mandat_administratif": { label: "Mandat Administratif MPR", icon: FileText, aide: "MPR" },
  "attestation_mpr": { label: "Attestation MPR", icon: FileCheck, aide: "MPR" },
  "fiche_standardisee": { label: "Fiche Standardisée", icon: FileText, aide: "CEE" },
  "synthese_aides": { label: "Synthèse des Aides", icon: FileText, aide: "TOUS" },
  // TEF / MaPrimeAdapt'
  "mandat_anah": { label: "Mandat ANAH", icon: FileText, aide: "MPA" },
  "devis_amo": { label: "Devis AMO", icon: FileOutput, aide: "MPA" },
  "contrat_amo": { label: "Contrat AMO", icon: FileSignature, aide: "MPA" },
  "bordereau_retractation": { label: "Bordereau de rétractation", icon: FileText, aide: "MPA" },
  "attestation_mpa": { label: "Attestation MPA", icon: FileCheck, aide: "MPA" },
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export default function DocumentsAidesTab({ devisId, devis, tenantId, onRefreshDevis }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [documentsGeneres, setDocumentsGeneres] = useState([]);
  const [preconditions, setPreconditions] = useState(null);
  
  // États UI
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [showSnapshotDialog, setShowSnapshotDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Fonction pour obtenir les headers avec authentification (toujours frais)
  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    if (!tenantId) {
      console.error("[DocumentsAidesTab] tenantId manquant — impossible d'appeler l'API.");
    }
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // ==========================================================================
  // CHARGEMENT DES DONNÉES
  // ==========================================================================

  const loadData = useCallback(async () => {
    if (!devisId) return;
    
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      // Charger en parallèle
      const [templatesRes, documentsRes, preconditionsRes] = await Promise.all([
        fetch(`${API}/api/metier/documents-engine/templates`, { headers }),
        fetch(`${API}/api/metier/documents-engine/devis/${devisId}/documents`, { headers }),
        fetch(`${API}/api/metier/documents-engine/devis/${devisId}/preconditions?phase=avant_travaux`, { headers })
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data);
      }

      if (documentsRes.ok) {
        const data = await documentsRes.json();
        setDocumentsGeneres(data);
      }

      if (preconditionsRes.ok) {
        const data = await preconditionsRes.json();
        setPreconditions(data);
      }
    } catch (error) {
      console.error("Erreur chargement documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  }, [devisId, tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==========================================================================
  // LOGIQUE MÉTIER
  // ==========================================================================

  const statutDevis = devis?.statut || "draft";
  const ordreStatutActuel = STATUTS_ORDRE[statutDevis] ?? 0;
  
  // Vérifier si le devis est au statut PAYE (verrouillage définitif)
  const isVerrouilleDefinitif = ordreStatutActuel >= STATUTS_ORDRE["paye"];
  
  // Vérifier si le devis est au statut DOSSIER_DEPOSE (traçable mais modifiable)
  const isDossierDepose = ordreStatutActuel === STATUTS_ORDRE["dossier_depose"];

  // Vérifier si un template est générable
  // RÈGLE MODIFIÉE: Génération possible dès VALIDATION, bloquée uniquement à PAYE
  const isTemplateGenerable = (template) => {
    if (isVerrouilleDefinitif) return false; // Bloqué si PAYE
    
    // Si le template requiert "locked", on accepte aussi "validation"
    let ordreRequis = STATUTS_ORDRE[template.statut_requis] ?? 0;
    if (ordreRequis === STATUTS_ORDRE["locked"]) {
      ordreRequis = STATUTS_ORDRE["validation"]; // Abaisser le seuil
    }
    
    return ordreStatutActuel >= ordreRequis;
  };

  // Obtenir le message de blocage pour un template
  const getMessageBlocage = (template) => {
    if (isVerrouilleDefinitif) {
      return "Dossier payé - Verrouillage définitif. Aucune modification possible.";
    }
    if (isTemplateGenerable(template)) return null;
    
    const statutRequis = STATUT_LABELS[template.statut_requis] || template.statut_requis;
    const statutActuel = STATUT_LABELS[statutDevis] || statutDevis;
    
    return `Document non disponible : le devis doit être "${statutRequis}" (actuel: "${statutActuel}")`;
  };

  // Grouper les documents générés par template
  const documentsParTemplate = documentsGeneres.reduce((acc, doc) => {
    const code = doc.template_code;
    if (!acc[code]) acc[code] = [];
    acc[code].push(doc);
    return acc;
  }, {});

  // Trier par version descendante
  Object.keys(documentsParTemplate).forEach(code => {
    documentsParTemplate[code].sort((a, b) => (b.version_document || 0) - (a.version_document || 0));
  });

  // Statistiques
  const totalDocuments = documentsGeneres.length;
  const documentsSigned = documentsGeneres.filter(d => d.est_signe).length;
  const documentsVerrouilles = documentsGeneres.filter(d => d.verrouille_definitif).length;
  const templatesGenerables = templates.filter(isTemplateGenerable).length;

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  const genererDocument = async (templateCode) => {
    if (isVerrouilleDefinitif) {
      toast.error("Dossier payé - Aucune modification possible.");
      return;
    }
    
    setGenerating(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(
        `${API}/api/metier/documents-engine/generer/${devisId}?mode=unitaire&template_code=${templateCode}`,
        { method: "POST", headers }
      );

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.documents?.length > 0) {
          toast.success(`Document "${data.documents[0].template_code}" généré (v${data.documents[0].version_document})`);
          loadData();
        } else if (data.erreurs?.length > 0) {
          toast.error(data.erreurs[0]);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la génération");
      }
    } catch (error) {
      console.error("Erreur génération:", error);
      toast.error("Erreur lors de la génération du document");
    } finally {
      setGenerating(false);
    }
  };

  const signerDocument = async (documentId) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(
        `${API}/api/metier/documents-engine/devis/${devisId}/documents/${documentId}/signer`,
        { method: "POST", headers }
      );

      if (res.ok) {
        toast.success("Document signé (traçabilité). Régénération possible si nécessaire.");
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la signature");
      }
    } catch (error) {
      console.error("Erreur signature:", error);
      toast.error("Erreur lors de la signature");
    }
  };

  // Aperçu PDF - Ouvre le PDF réel généré côté serveur avec authentification
  const apercuPDF = async (document, usePremiumFormat = false) => {
    // Utiliser fetch avec headers pour éviter "Tenant non identifié"
    const endpoint = usePremiumFormat ? "apercu-v2" : "apercu";
    const url = `${API}/api/metier/documents-engine/devis/${devisId}/documents/${document.id}/${endpoint}`;
    
    try {
      const authHeaders = getAuthHeaders();
      
      const response = await fetch(url, { headers: authHeaders });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Erreur ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      
      // Nettoyer après un délai
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      console.error("Erreur aperçu:", err);
      alert(`Erreur: ${err.message}`);
    }
  };

  // Télécharger PDF - Télécharge le PDF réel généré côté serveur
  const telechargerDocument = async (document, usePremiumFormat = false) => {
    try {
      const endpoint = usePremiumFormat ? "pdf-v2" : "pdf";
      const url = `${API}/api/metier/documents-engine/devis/${devisId}/documents/${document.id}/${endpoint}`;
      
      const headers = getAuthHeaders();
      const res = await fetch(url, { headers });
      
      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const a = window.document.createElement("a");
        a.href = downloadUrl;
        const suffix = usePremiumFormat ? "_premium" : "";
        a.download = `${document.template_code}_v${document.version_document}${suffix}.pdf`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
        toast.success(usePremiumFormat ? "PDF Premium téléchargé" : "PDF téléchargé");
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  // Vérifier si un template supporte le format Premium
  const supportsPremiumFormat = (templateCode) => {
    return templateCode?.startsWith("CC_CEE") || templateCode === "CC_CEE_PREMIUM_2025";
  };

  const voirSnapshot = (document) => {
    setSelectedDocument(document);
    setShowSnapshotDialog(true);
  };

  // ==========================================================================
  // FORMAT HELPERS
  // ==========================================================================

  const formatDate = formatDateTimeFR;

  // ==========================================================================
  // RENDU
  // ==========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  // Vérifier si le devis est avant VALIDATION (génération impossible)
  // MODIFIÉ: Génération possible dès VALIDATION (pas besoin LOCKED)
  const isAvantLocked = ordreStatutActuel < STATUTS_ORDRE["validation"];

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="documents-aides-tab">
        {/* ================================================================= */}
        {/* BLOC 3 : STATUT DOCUMENTAIRE (en premier pour vue d'ensemble) */}
        {/* ================================================================= */}
        <Card className={`bg-gradient-to-r border ${
          isVerrouilleDefinitif 
            ? "from-red-900/30 to-red-800/20 border-[var(--oh-error-border)]" 
            : isDossierDepose 
            ? "from-blue-900/30 to-blue-800/20 border-[var(--oh-accent)]"
            : "from-slate-800/80 to-slate-800/50 border-[var(--oh-border)]"
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Badge className={`${
                    isVerrouilleDefinitif 
                      ? "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]"
                      : ordreStatutActuel >= STATUTS_ORDRE["locked"] 
                      ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" 
                      : "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]"
                  }`}>
                    {STATUT_LABELS[statutDevis] || statutDevis}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[var(--oh-info-text)]" />
                    <span className="text-[var(--oh-text-muted)]">Phase active:</span>
                    <span className="text-[var(--oh-text)] font-medium">
                      {ordreStatutActuel >= STATUTS_ORDRE["chantier_termine"] ? "Après travaux" : "Avant travaux"}
                    </span>
                  </div>
                  
                  <div className="h-4 w-px bg-slate-600" />
                  
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[var(--oh-accent-text)]" />
                    <span className="text-[var(--oh-text-muted)]">Disponibles:</span>
                    <span className="text-[var(--oh-text)] font-medium">{templatesGenerables}</span>
                  </div>
                  
                  <div className="h-4 w-px bg-slate-600" />
                  
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[var(--oh-success-text)]" />
                    <span className="text-[var(--oh-text-muted)]">Signés:</span>
                    <span className="text-[var(--oh-success-text)] font-medium">{documentsSigned}/{totalDocuments}</span>
                  </div>
                </div>
              </div>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadData}
                className="border-[var(--oh-border)]"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ================================================================= */}
        {/* MESSAGE SI VERROUILLAGE DÉFINITIF (PAYE) */}
        {/* ================================================================= */}
        {isVerrouilleDefinitif && (
          <Card className="bg-[var(--oh-error-bg)] border-[var(--oh-error-border)]">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-[var(--oh-error-text)] flex-shrink-0" />
                <div>
                  <p className="text-[var(--oh-error-text)] font-medium">Dossier payé - Verrouillage définitif</p>
                  <p className="text-[var(--oh-error-text)] text-sm">
                    Aucune modification n'est possible. Les documents sont définitivement verrouillés.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ================================================================= */}
        {/* MESSAGE SI DOSSIER DEPOSE (traçable mais modifiable) */}
        {/* ================================================================= */}
        {isDossierDepose && !isVerrouilleDefinitif && (
          <Card className="bg-[var(--oh-accent-bg)] border-[var(--oh-accent)]">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-[var(--oh-accent-text)] flex-shrink-0" />
                <div>
                  <p className="text-[var(--oh-accent-text)] font-medium">Dossier déposé - Documents modifiables</p>
                  <p className="text-[var(--oh-accent-text)] text-sm">
                    Le dépôt est tracé mais les documents peuvent encore être régénérés si nécessaire.
                    Le verrouillage définitif interviendra au statut "Payé".
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ================================================================= */}
        {/* MESSAGE SI DEVIS AVANT VALIDATION (brouillon) */}
        {/* ================================================================= */}
        {isAvantLocked && (
          <Card className="bg-[var(--oh-info-bg)] border-[var(--oh-info-border)]">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-[var(--oh-info-text)] flex-shrink-0" />
                <div>
                  <p className="text-[var(--oh-info-text)] font-medium">Documents non disponibles</p>
                  <p className="text-[var(--oh-info-text)] text-sm">
                    Le devis doit être au moins en validation pour générer les documents CEE/MPR. 
                    Statut actuel: <strong>{STATUT_LABELS[statutDevis] || statutDevis}</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ================================================================= */}
        {/* BLOC 1 : DOCUMENTS AUTORISÉS */}
        {/* ================================================================= */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[var(--oh-accent-text)]" />
                  Documents générables
                </CardTitle>
                <CardDescription className="text-[var(--oh-text-muted)] mt-1">
                  Liste dynamique selon le statut du devis {isVerrouilleDefinitif && "(lecture seule)"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-3" />
                <p className="text-[var(--oh-text-muted)]">Aucun template disponible</p>
                <p className="text-[var(--oh-text-secondary)] text-sm">Contactez l'administrateur</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map((template) => {
                  const isGenerable = isTemplateGenerable(template);
                  const messageBlocage = getMessageBlocage(template);
                  const phaseInfo = PHASES[template.phase] || PHASES.avant_travaux;
                  const PhaseIcon = phaseInfo.icon;
                  const versionsExistantes = documentsParTemplate[template.code] || [];
                  const derniereVersion = versionsExistantes[0];
                  const aVersionSignee = versionsExistantes.some(v => v.est_signe);

                  return (
                    <div 
                      key={template.id || template.code}
                      className={`p-4 rounded-lg border transition-all ${
                        isGenerable 
                          ? "bg-[var(--oh-bg-surface)] border-[var(--oh-border)] hover:border-[var(--oh-border)]" 
                          : "bg-[var(--oh-bg-surface)] border-[var(--oh-border)] opacity-60"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${isGenerable ? "bg-[var(--oh-accent-bg)]" : "bg-[var(--oh-bg-elevated)]"}`}>
                            <FileText className={`w-5 h-5 ${isGenerable ? "text-[var(--oh-accent-text)]" : "text-[var(--oh-text-secondary)]"}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-[var(--oh-text)] font-medium">{template.nom}</h4>
                              <Badge className={phaseInfo.color} variant="outline">
                                <PhaseIcon className="w-3 h-3 mr-1" />
                                {phaseInfo.label}
                              </Badge>
                              {template.aide_type && (
                                <Badge className={
                                  template.aide_type === "cee" 
                                    ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]"
                                    : template.aide_type === "mpr"
                                    ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]"
                                    : "bg-[var(--oh-stat-cyan-bg)] text-[var(--oh-stat-cyan-text)]"
                                }>
                                  {template.aide_type?.toUpperCase()}
                                </Badge>
                              )}
                            </div>
                            <p className="text-[var(--oh-text-secondary)] text-sm font-mono">{template.code}</p>
                            {versionsExistantes.length > 0 && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-[var(--oh-text-muted)]">
                                  {versionsExistantes.length} version(s)
                                </span>
                                {aVersionSignee && (
                                  <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">
                                    <ShieldCheck className="w-3 h-3 mr-1" />
                                    Signé
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {isGenerable ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => genererDocument(template.code)}
                                disabled={generating}
                                className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                                data-testid={`generate-${template.code}`}
                              >
                                {generating ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    {derniereVersion ? "Regénérer" : "Générer"}
                                  </>
                                )}
                              </Button>
                              {derniereVersion && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => telechargerDocument(derniereVersion)}
                                  title="Télécharger la dernière version"
                                >
                                  <Download className="w-4 h-4 text-[var(--oh-text-muted)]" />
                                </Button>
                              )}
                            </>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--oh-bg-elevated)] rounded-lg cursor-not-allowed">
                                  <Lock className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                                  <span className="text-xs text-[var(--oh-text-secondary)]">Non disponible</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs">
                                <p>{messageBlocage}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ================================================================= */}
        {/* BLOC 2 : HISTORIQUE DES VERSIONS */}
        {/* ================================================================= */}
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[var(--oh-info-text)]" />
              Historique des versions
            </CardTitle>
            <CardDescription className="text-[var(--oh-text-muted)]">
              Toutes les versions générées avec leur statut de signature
            </CardDescription>
          </CardHeader>
          <CardContent>
            {documentsGeneres.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-3" />
                <p className="text-[var(--oh-text-muted)]">Aucun document généré</p>
                <p className="text-[var(--oh-text-secondary)] text-sm">Les documents apparaîtront ici après génération</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(documentsParTemplate).map(([code, versions]) => {
                  const isExpanded = expandedDoc === code;
                  const template = templates.find(t => t.code === code);
                  
                  return (
                    <Collapsible 
                      key={code} 
                      open={isExpanded}
                      onOpenChange={() => setExpandedDoc(isExpanded ? null : code)}
                    >
                      <div className="bg-[var(--oh-bg-surface)] rounded-lg border border-[var(--oh-border)]">
                        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-[var(--oh-bg-elevated)] rounded-lg transition-colors">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-[var(--oh-accent-text)]" />
                            <div className="text-left">
                              <h4 className="text-[var(--oh-text)] font-medium">{template?.nom || code}</h4>
                              <p className="text-[var(--oh-text-secondary)] text-sm font-mono">{code}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)]">
                              {versions.length} version(s)
                            </Badge>
                            {versions.some(v => v.est_signe) && (
                              <ShieldCheck className="w-4 h-4 text-[var(--oh-success-text)]" />
                            )}
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-[var(--oh-text-muted)]" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-[var(--oh-text-muted)]" />
                            )}
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <div className="px-4 pb-4 space-y-2">
                            {versions.map((doc) => (
                              <div 
                                key={doc.id}
                                className={`p-3 rounded-lg border ${
                                  doc.verrouille_definitif
                                    ? "bg-[var(--oh-error-bg)] border-[var(--oh-error-border)]"
                                    : doc.est_signe 
                                    ? "bg-[var(--oh-success-bg)] border-[var(--oh-success-border)]" 
                                    : "bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <Badge className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] font-mono">
                                      v{doc.version_document}
                                    </Badge>
                                    <div className="text-sm">
                                      <div className="flex items-center gap-2 text-[var(--oh-text-muted)]">
                                        <Calendar className="w-3 h-3" />
                                        {formatDate(doc.created_at || doc.genere_at)}
                                      </div>
                                      {doc.genere_par && (
                                        <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] mt-0.5">
                                          <User className="w-3 h-3" />
                                          {doc.genere_par}
                                        </div>
                                      )}
                                    </div>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1 text-xs text-[var(--oh-text-secondary)] cursor-help">
                                          <Hash className="w-3 h-3" />
                                          {doc.hash_document?.slice(0, 12)}...
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="font-mono text-xs">{doc.hash_document}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {/* Badges de statut */}
                                    {doc.verrouille_definitif ? (
                                      <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)]">
                                        <Lock className="w-3 h-3 mr-1" />
                                        Verrouillé définitif
                                      </Badge>
                                    ) : doc.est_signe ? (
                                      <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]">
                                        <ShieldCheck className="w-3 h-3 mr-1" />
                                        Signé
                                      </Badge>
                                    ) : !isVerrouilleDefinitif && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => signerDocument(doc.id)}
                                        className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
                                        data-testid={`sign-${doc.id}`}
                                      >
                                        <FileSignature className="w-3 h-3 mr-1" />
                                        Signer
                                      </Button>
                                    )}
                                    
                                    {/* Bouton Aperçu PDF */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => apercuPDF(doc)}
                                          className="text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                                        >
                                          <Printer className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Aperçu PDF</TooltipContent>
                                    </Tooltip>
                                    
                                    {/* Bouton Aperçu Premium (si supporté) */}
                                    {supportsPremiumFormat(doc.template_code) && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => apercuPDF(doc, true)}
                                            className="text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
                                          >
                                            <FileOutput className="w-4 h-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Aperçu format Premium Energy</TooltipContent>
                                      </Tooltip>
                                    )}
                                    
                                    {/* Bouton Télécharger */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => telechargerDocument(doc)}
                                        >
                                          <Download className="w-4 h-4 text-[var(--oh-text-muted)]" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Télécharger PDF</TooltipContent>
                                    </Tooltip>
                                    
                                    {/* Bouton Télécharger Premium (si supporté) */}
                                    {supportsPremiumFormat(doc.template_code) && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => telechargerDocument(doc, true)}
                                            className="text-[var(--oh-info-text)]"
                                          >
                                            <Download className="w-4 h-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Télécharger format Premium Energy</TooltipContent>
                                      </Tooltip>
                                    )}
                                    
                                    {/* Bouton Snapshot */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => voirSnapshot(doc)}
                                        >
                                          <Eye className="w-4 h-4 text-[var(--oh-text-muted)]" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Voir le snapshot des données</TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ================================================================= */}
        {/* DIALOG SNAPSHOT */}
        {/* ================================================================= */}
        <Dialog open={showSnapshotDialog} onOpenChange={setShowSnapshotDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
            <DialogHeader>
              <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
                <Info className="w-5 h-5 text-[var(--oh-accent-text)]" />
                Snapshot du document
              </DialogTitle>
            </DialogHeader>
            
            {selectedDocument && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[var(--oh-text-secondary)]">Template:</span>
                    <span className="text-[var(--oh-text)] ml-2">{selectedDocument.template_code}</span>
                  </div>
                  <div>
                    <span className="text-[var(--oh-text-secondary)]">Version:</span>
                    <span className="text-[var(--oh-text)] ml-2">v{selectedDocument.version_document}</span>
                  </div>
                  <div>
                    <span className="text-[var(--oh-text-secondary)]">Généré le:</span>
                    <span className="text-[var(--oh-text)] ml-2">{formatDate(selectedDocument.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-[var(--oh-text-secondary)]">Hash:</span>
                    <span className="text-[var(--oh-text-muted)] ml-2 font-mono text-xs">
                      {selectedDocument.hash_document?.slice(0, 24)}...
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="text-[var(--oh-text)] font-medium mb-2">Données snapshotées</h4>
                  <pre className="bg-[var(--oh-bg-elevated)] p-4 rounded-lg overflow-x-auto text-xs text-[var(--oh-text-muted)] max-h-96">
                    {JSON.stringify(selectedDocument.snapshot_complet, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSnapshotDialog(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
